import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { database } from '../../services/firebase';
import { ref, get, update } from 'firebase/database';

const Education = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [educationList, setEducationList] = useState([{
    educationLevel: '',
    institution: '',
    fieldOfStudy: '',
    startYear: '',
    endYear: '',
    cgpa: '',
    achievements: '',
    currentlyStudying: false
  }]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const userId = localStorage.getItem('userId');
    const profileStep = localStorage.getItem('profileStep');
    
    if (!userId) {
      navigate('/login');
      return;
    }

    const initializeEducation = async () => {
      try {
        const userRef = ref(database, `users/${userId}`);
        const snapshot = await get(userRef);
        
        if (snapshot.exists()) {
          const userData = snapshot.val();
          
          // Check both localStorage and database profileStep
          if (!profileStep || profileStep < '3' || userData.profileStep < 3) {
            navigate('/profile/address');
            return;
          }
          
          if (userData.education && userData.education.length > 0) {
            setEducationList(userData.education);
          }
        }
      } catch (error) {
        console.error('Error:', error);
        setError('Failed to load education data');
      } finally {
        setLoading(false);
      }
    };

    initializeEducation();
  }, [navigate]);

  const handleChange = (index, e) => {
    const { name, value, type, checked } = e.target;
    const list = [...educationList];
    list[index][name] = type === 'checkbox' ? checked : value;
    setEducationList(list);
  };

  const handleAddMore = () => {
    setEducationList([...educationList, {
      educationLevel: '',
      institution: '',
      fieldOfStudy: '',
      startYear: '',
      endYear: '',
      cgpa: '',
      achievements: '',
      currentlyStudying: false
    }]);
  };

  const handleRemove = (index) => {
    if (educationList.length > 1) {
      const list = [...educationList];
      list.splice(index, 1);
      setEducationList(list);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const userId = localStorage.getItem('userId');
      if (!userId) {
        throw new Error('User not found');
      }

      const userRef = ref(database, `users/${userId}`);
      const updates = {
        education: educationList,
        profileStep: 4,
        updatedAt: new Date().toISOString()
      };

      await update(userRef, updates);
      localStorage.setItem('profileStep', '4');
      navigate('/profile/skills');
    } catch (error) {
      console.error('Error:', error);
      setError('Failed to update education details');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f9fe]">
      <div className="fixed top-0 left-0 right-0 h-1 bg-gray-200">
        <div className="h-full w-3/4 bg-gradient-to-r from-blue-500 to-indigo-600 transition-all duration-300"></div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Educational Background
          </h2>
          <div className="flex items-center justify-center space-x-8 mb-8">
            {[1, 2].map(step => (
              <div key={step} className="flex items-center opacity-50">
                <div className="w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center font-semibold">✓</div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-600">Step {step}</p>
                </div>
              </div>
            ))}
            <div className="flex items-center">
              <div className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center font-semibold">3</div>
              <div className="ml-3">
                <p className="text-sm font-medium text-indigo-600">Education</p>
              </div>
            </div>
            <div className="flex items-center opacity-50">
              <div className="w-8 h-8 rounded-full border-2 border-gray-300 text-gray-400 flex items-center justify-center font-semibold">4</div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-400">Skills</p>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-lg shadow-sm">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

         <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <form onSubmit={handleSubmit} className="divide-y divide-gray-100">
            {educationList.map((education, index) => (
              <div key={index} className="p-8">
                <div className="flex items-center mb-6">
                  <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center mr-4">
                    <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l9-5-9-5-9 5 9 5z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    {index === 0 ? 'Highest Education' : `Additional Education ${index + 1}`}
                  </h3>
                </div>

                <div className="space-y-6">
                  <div className="grid grid-cols-1 gap-y-6 gap-x-6 sm:grid-cols-2">
                    <div className="group">
                      <label htmlFor={`educationLevel-${index}`} className="block text-sm font-medium text-gray-700 mb-2">
                        Education Level
                      </label>
                      <select
                        id={`educationLevel-${index}`}
                        name="educationLevel"
                        required
                        className="block w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        value={education.educationLevel}
                        onChange={(e) => handleChange(index, e)}
                      >
                        <option value="">Select Education Level</option>
                        <option value="high_school">High School</option>
                        <option value="bachelors">Bachelor's Degree</option>
                        <option value="masters">Master's Degree</option>
                        <option value="phd">Ph.D.</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div className="group">
                      <label htmlFor={`institution-${index}`} className="block text-sm font-medium text-gray-700 mb-2">
                        Institution Name
                      </label>
                      <input
                        type="text"
                        id={`institution-${index}`}
                        name="institution"
                        required
                        className="block w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        value={education.institution}
                        onChange={(e) => handleChange(index, e)}
                      />
                    </div>
                  </div>

                  {/* Continue updating other fields similarly */}
                  <div className="group">
                    <label htmlFor={`fieldOfStudy-${index}`} className="block text-sm font-medium text-gray-700 mb-2">
                      Field of Study
                    </label>
                    <input
                      type="text"
                      id={`fieldOfStudy-${index}`}
                      name="fieldOfStudy"
                      required
                      className="block w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      value={education.fieldOfStudy}
                      onChange={(e) => handleChange(index, e)}
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-y-6 gap-x-6 sm:grid-cols-2">
                    <div className="group">
                      <label htmlFor={`startYear-${index}`} className="block text-sm font-medium text-gray-700 mb-2">
                        Start Year
                      </label>
                      <input
                        type="number"
                        id={`startYear-${index}`}
                        name="startYear"
                        required
                        min="1900"
                        max="2099"
                        className="block w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        value={education.startYear}
                        onChange={(e) => handleChange(index, e)}
                      />
                    </div>

                    <div className="group">
                      <label htmlFor={`endYear-${index}`} className="block text-sm font-medium text-gray-700 mb-2">
                        End Year
                      </label>
                      <input
                        type="number"
                        id={`endYear-${index}`}
                        name="endYear"
                        min="1900"
                        max="2099"
                        className="block w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        value={education.endYear}
                        onChange={(e) => handleChange(index, e)}
                        disabled={education.currentlyStudying}
                      />
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id={`currentlyStudying-${index}`}
                      name="currentlyStudying"
                      className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      checked={education.currentlyStudying}
                      onChange={(e) => handleChange(index, e)}
                    />
                    <label htmlFor={`currentlyStudying-${index}`} className="text-sm text-gray-900">
                      I am currently studying here
                    </label>
                  </div>

                  <div className="group">
                    <label htmlFor={`cgpa-${index}`} className="block text-sm font-medium text-gray-700 mb-2">
                      CGPA / Percentage
                    </label>
                    <input
                      type="text"
                      id={`cgpa-${index}`}
                      name="cgpa"
                      className="block w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      value={education.cgpa}
                      onChange={(e) => handleChange(index, e)}
                    />
                  </div>

                  <div className="group">
                    <label htmlFor={`achievements-${index}`} className="block text-sm font-medium text-gray-700 mb-2">
                      Achievements & Activities
                    </label>
                    <textarea
                      id={`achievements-${index}`}
                      name="achievements"
                      rows="4"
                      className="block w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      value={education.achievements}
                      onChange={(e) => handleChange(index, e)}
                    ></textarea>
                  </div>

                  {educationList.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemove(index)}
                      className="flex items-center px-4 py-2 mt-4 text-red-600 hover:text-red-800 transition-colors duration-200"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Remove Entry
                    </button>
                  )}
                </div>
              </div>
            ))}

            {/* After the "Add Another Education" button */}
            <div className="p-8 border-t border-gray-100">
              <button
                type="button"
                onClick={handleAddMore}
                className="flex items-center justify-center w-full px-6 py-3 mb-6 text-indigo-600 bg-indigo-50 rounded-xl hover:bg-indigo-100 transition-colors duration-200"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add Another Education
              </button>

              <div className="flex justify-between items-center mt-8">
                <button
                  type="button"
                  onClick={() => navigate('/profile/address')}
                  className="flex items-center px-6 py-3 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors duration-200"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                  </svg>
                  Previous Step
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex items-center px-8 py-3 text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 transition-colors duration-200 disabled:opacity-50"
                >
                  {loading ? 'Saving...' : (
                    <>
                      Next: Skills
                      <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Education;